import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useNavigate } from 'react-router-dom';
import { sessionsApi } from '@/lib/api-client';
import { useAuthStore } from '@/features/auth/store/authStore';

export interface QuizAnswer {
  question_id: string;
  selected_answer: string;
  time_spent_seconds: number;
}

export function useStartSession() {
  const user = useAuthStore((state) => state.user);

  return useMutation({
    mutationFn: (storyId: string) => {
      if (!user?.id) throw new Error('User not authenticated');
      return sessionsApi.startSession(user.id, storyId);
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Session Error',
        message: error.message,
        color: 'red',
      });
    },
  });
}

export function useSubmitQuiz() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sessionId,
      answers,
      readingTime,
      quizStartedAt,
    }: {
      sessionId: string;
      answers: QuizAnswer[];
      readingTime: number;
      quizStartedAt: Date;
      questionTimes: Record<number, number>;
      storyId?: string;
    }) =>
      sessionsApi.submitSession({
        session_id: sessionId,
        answers,
        time_spent_reading_seconds: readingTime,
        quiz_started_at: quizStartedAt.toISOString(),
      }),
    onSuccess: (result, variables) => {
      // Invalidate library cache so it refreshes with the new story_read_end activity
      // (which is now created automatically by the backend when quiz is submitted)
      queryClient.invalidateQueries({ queryKey: ['library'] });
      queryClient.invalidateQueries({ queryKey: ['activities', 'story_read_end'] });

      navigate(`/results/${variables.sessionId}`, { state: { result, storyId: variables.storyId } });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Submission Failed',
        message: error.message,
        color: 'red',
      });
    },
  });
}
